import type { Result } from "neverthrow";
import { err, ok } from "neverthrow";

import { buildInvalidError, type InvalidError } from "@thalamos/common";

import {
  toResponseRows,
  type SubmissionDocumentUnbranded,
} from "../../assessments/document.js";
import { isNotAnswered } from "../../assessments/notAnswered.js";
import type { PublishedInstrumentSchema } from "../../assessments/publishedSchema.js";
import {
  instrumentIssuesIn,
  pointerFor,
  type InstrumentIssue,
} from "../../assessments/validate.js";
import type { FormContext, Problem } from "./checks.js";
import type { Banner, FormElement } from "./plainForm.js";

type ItemElement = Extract<FormElement, { kind: "item" }>;

/** A problem with a document, and where in it. */
export type FormProblem = Problem & {
  /**
   * Whether the problem is with an answer given, or with something needed
   * and not given: a part-filled document is expected to have the second, so
   * a collector may choose when to show those.
   */
  readonly kind: "answer" | "missing";
  /** A JSON pointer into the document: an item, its response, or a group. */
  readonly at: string;
  /** What the instrument's validator said, for a developer; never shown to a respondent. */
  readonly detail?: string;
};

/** Whether a group or item is asked by its own rule, given the answers so far. */
const isAsked = (
  element: FormElement,
  answers: ReadonlyMap<string, unknown>,
  context: FormContext,
): boolean => {
  const { when } = element.asking;

  if (when === "always") {
    return true;
  }

  return (
    when !== "never" && when({ answers: Object.fromEntries(answers), context })
  );
};

/** Whether an item's comment is required by its own rule, given the answers the whole form leaves. */
const isCommentRequired = (
  { asking: { commentRequired } }: ItemElement,
  answers: ReadonlyMap<string, unknown>,
  context: FormContext,
): boolean => {
  if (commentRequired === "always") {
    return true;
  }

  return (
    commentRequired !== "never" &&
    commentRequired({ answers: Object.fromEntries(answers), context })
  );
};

/** Whether a banner is shown by its own rule, given the answers the whole form leaves. */
const isShown = (
  { shownWhen }: Banner,
  answers: ReadonlyMap<string, unknown>,
  context: FormContext,
): boolean =>
  shownWhen === undefined ||
  shownWhen({ answers: Object.fromEntries(answers), context });

const itemsOf = (form: readonly FormElement[]): ItemElement[] =>
  form.filter((element): element is ItemElement => element.kind === "item");

/** Whether the document holds anything at a path. */
const isPresent = (
  document: SubmissionDocumentUnbranded,
  path: readonly PropertyKey[],
): boolean => {
  const group = path[0];
  const item = path[1];

  if (typeof group !== "string") {
    return true;
  }

  const answers = document[group];

  return typeof item === "string"
    ? answers?.[item] !== undefined
    : answers !== undefined;
};

/** Where an answer sits in a document, as a JSON pointer to its item. */
const answerAt = (group: string, item: string): string =>
  pointerFor([group, item]);

/**
 * Where each answer whose response the instrument refuses sits: refused at
 * the item itself or within its response. A comment is an answer of its own,
 * so a refused one leaves the response it is about standing.
 */
const refusedAnswersIn = (
  issues: readonly InstrumentIssue[],
  document: SubmissionDocumentUnbranded,
): Set<string> =>
  new Set(
    issues.flatMap(({ path }) => {
      const group = path[0];
      const item = path[1];
      const within = path[2];

      return typeof group === "string" &&
        typeof item === "string" &&
        (within === undefined || within === "response") &&
        isPresent(document, path)
        ? [answerAt(group, item)]
        : [];
    }),
  );

/**
 * Each response filed where the form asks its item, by item key, leaving out
 * every response the instrument refuses, and every item sent not answered.
 *
 * Only these may be handed to a rule or a check, which are typed as though
 * every response were one its item accepts. An answer filed under a group
 * that has no such item is left out too, because the instrument refuses
 * it at the group rather than at the answer. An item not answered reads to
 * every rule as an item with no answer, so a domain it would open stays
 * closed.
 */
const acceptedAnswersIn = (
  form: readonly FormElement[],
  document: SubmissionDocumentUnbranded,
  refused: ReadonlySet<string>,
): Map<string, unknown> => {
  const asked = new Set(
    itemsOf(form).map((element) => answerAt(element.at.group, element.at.item)),
  );

  return new Map(
    toResponseRows(document)
      .filter(({ group, item, response }) => {
        const at = answerAt(group, item);

        return asked.has(at) && !refused.has(at) && !isNotAnswered(response);
      })
      .map(({ item, response }) => [item, response]),
  );
};

/** The keys of the groups and items a form does not ask. */
export type NotAsked = {
  readonly groups: ReadonlySet<string>;
  readonly items: ReadonlySet<string>;
};

/**
 * One pass in the form's order: an item found unasked, by its own rule or its
 * group's, stops counting as answered for everything after it, so a rule
 * reading it sees it absent. A group is not asked where it asks none of its
 * items, which is so of every group its own rule hides. Also gives the
 * answers left once every unasked item's is dropped.
 */
const notAskedAmong = (
  form: readonly FormElement[],
  accepted: ReadonlyMap<string, unknown>,
  context: FormContext,
): NotAsked & { readonly answers: ReadonlyMap<string, unknown> } => {
  const answers = new Map(accepted);
  const hiddenByRule = new Set<string>();
  const items = new Set<string>();

  for (const element of form) {
    if (element.kind === "group") {
      if (!isAsked(element, answers, context)) {
        hiddenByRule.add(element.at.group);
      }
    } else if (
      hiddenByRule.has(element.at.group) ||
      !isAsked(element, answers, context)
    ) {
      items.add(element.at.item);
      answers.delete(element.at.item);
    }
  }

  const asking = new Set(
    itemsOf(form)
      .filter((element) => !items.has(element.at.item))
      .map((element) => element.at.group),
  );

  return {
    groups: new Set(
      form.flatMap((element) =>
        element.kind === "group" && !asking.has(element.at.group)
          ? [element.at.group]
          : [],
      ),
    ),
    items,
    answers,
  };
};

/**
 * What a form does not ask for a document, and the answers left once those
 * to every unasked item are dropped: each asked item's response the
 * instrument accepts, by item key.
 */
export const judgedAnswersIn = ({
  schema,
  form,
  document,
  context,
}: {
  schema: PublishedInstrumentSchema;
  form: readonly FormElement[];
  document: SubmissionDocumentUnbranded;
  context: FormContext;
}) =>
  instrumentIssuesIn(schema, document).map((issues) =>
    notAskedAmong(
      form,
      acceptedAnswersIn(form, document, refusedAnswersIn(issues, document)),
      context,
    ),
  );

/**
 * The groups and items a form does not ask, given a document's answers,
 * part-filled or complete.
 *
 * Rules read only the responses the instrument accepts; a response it refuses
 * counts as absent. Errs only where no validator can be built from the schema.
 */
export const notAsked = ({
  schema,
  form,
  document,
  context,
}: {
  schema: PublishedInstrumentSchema;
  form: readonly FormElement[];
  document: SubmissionDocumentUnbranded;
  context: FormContext;
}): Result<NotAsked, InvalidError> =>
  judgedAnswersIn({ schema, form, document, context }).map(
    ({ groups, items }) => ({ groups, items }),
  );

/**
 * The form with only the banners its rules show, given a document's answers,
 * part-filled or complete: each rule reads the answers left once the whole
 * form is settled, so a banner never reacts to an answer its question's rules
 * count as absent, whether refused by the instrument or belonging to an item
 * not asked.
 *
 * Errs only where no validator can be built from the schema.
 */
export const withBannersShown = ({
  schema,
  form,
  document,
  context,
}: {
  schema: PublishedInstrumentSchema;
  form: readonly FormElement[];
  document: SubmissionDocumentUnbranded;
  context: FormContext;
}): Result<FormElement[], InvalidError> =>
  judgedAnswersIn({ schema, form, document, context }).map(({ answers }) =>
    form.map((element) =>
      element.kind === "item"
        ? {
            ...element,
            presentation: {
              ...element.presentation,
              banners: element.presentation.banners.filter((banner) =>
                isShown(banner, answers, context),
              ),
            },
          }
        : element,
    ),
  );

/**
 * The items whose comment the form requires, given a document's answers,
 * part-filled or complete, by item key: each rule reads the answers left once
 * the whole form is settled, as a banner's does. Says nothing of whether the
 * item is asked or answered, before which no comment is due.
 *
 * Errs only where no validator can be built from the schema.
 */
export const commentsRequired = ({
  schema,
  form,
  document,
  context,
}: {
  schema: PublishedInstrumentSchema;
  form: readonly FormElement[];
  document: SubmissionDocumentUnbranded;
  context: FormContext;
}): Result<ReadonlySet<string>, InvalidError> =>
  judgedAnswersIn({ schema, form, document, context }).map(
    ({ answers }) =>
      new Set(
        itemsOf(form)
          .filter((element) => isCommentRequired(element, answers, context))
          .map((element) => element.at.item),
      ),
  );

/**
 * What the instrument refuses, as problems a collector can word.
 *
 * Something missing is reported as needing an answer, the same as an item the
 * form insists on, because that is what a respondent can do about it. A
 * required group missing whole is reported at each item it requires, so each
 * question shows what it needs.
 */
const instrumentProblems = (
  issues: readonly InstrumentIssue[],
  items: readonly ItemElement[],
  document: SubmissionDocumentUnbranded,
): FormProblem[] =>
  issues.flatMap(({ path, message }): FormProblem[] => {
    if (isPresent(document, path)) {
      return [
        {
          kind: "answer",
          at: pointerFor(path),
          code: "not-valid-for-instrument",
          detail: message,
        },
      ];
    }

    const requiredInGroup = items.filter(
      (element) =>
        path.length === 1 &&
        element.at.group === path[0] &&
        element.asking.required === "by_instrument",
    );

    return requiredInGroup.length === 0
      ? [
          {
            kind: "missing",
            at: pointerFor(path.slice(0, 2)),
            code: "must-be-answered",
          },
        ]
      : requiredInGroup.map((element) => ({
          kind: "missing",
          at: pointerFor([element.at.group, element.at.item]),
          code: "must-be-answered",
        }));
  });

/** What the form refuses beyond the instrument. */
const formProblems = (
  form: readonly FormElement[],
  document: SubmissionDocumentUnbranded,
  /** Where each answer whose response the instrument refuses sits. */
  refused: ReadonlySet<string>,
  context: FormContext,
): FormProblem[] => {
  const items = itemsOf(form);
  const { items: hidden, answers } = notAskedAmong(
    form,
    acceptedAnswersIn(form, document, refused),
    context,
  );
  // A refused answer, or an item sent not answered, still holds a response:
  // neither is also missing.
  const rows = toResponseRows(document);
  const responded = new Set(
    rows.map(({ group, item }) => answerAt(group, item)),
  );
  // A comment is about a rating, so an item sent not answered has nothing for
  // one to be about.
  const rated = new Set(
    rows.flatMap(({ group, item, response }) =>
      isNotAnswered(response) ? [] : [answerAt(group, item)],
    ),
  );
  // A comment of only spaces says nothing, so never satisfies one the form
  // requires.
  const commented = ({
    group,
    item,
  }: {
    group: string;
    item: string;
  }): boolean => {
    const comment = document[group]?.[item]?.response;

    return typeof comment === "string" && comment.trim() !== "";
  };
  const commentedItems = new Map(
    items.flatMap((element) =>
      element.commentAt === undefined
        ? []
        : [
            [
              answerAt(element.commentAt.group, element.commentAt.item),
              element,
            ] as const,
          ],
    ),
  );

  // As with the instrument's own requirements, an item the form insists on is
  // left out with an optional group the document leaves out.
  const optionalGroups = new Set(
    form.flatMap((element) =>
      element.kind === "group" && element.asking.required === "no"
        ? [element.at.group]
        : [],
    ),
  );
  const unanswered = items
    .filter(
      (element) =>
        element.asking.required === "by_form" &&
        !hidden.has(element.at.item) &&
        !(
          optionalGroups.has(element.at.group) &&
          document[element.at.group] === undefined
        ) &&
        !responded.has(answerAt(element.at.group, element.at.item)),
    )
    .map((element): FormProblem => ({
      kind: "missing",
      at: pointerFor([element.at.group, element.at.item]),
      code: "must-be-answered",
    }));

  // Only an answer the form asks, whose response the instrument accepts, is
  // in `answers`, so a comment is due only there.
  const uncommented = items.flatMap((element): FormProblem[] =>
    element.commentAt !== undefined &&
    answers.has(element.at.item) &&
    isCommentRequired(element, answers, context) &&
    !commented(element.commentAt)
      ? [
          {
            kind: "missing",
            at: pointerFor([
              element.commentAt.group,
              element.commentAt.item,
              "response",
            ]),
            code: "must-have-a-comment",
          },
        ]
      : [],
  );

  const answerProblems = toResponseRows(document)
    .filter(({ group, item }) => !refused.has(answerAt(group, item)))
    .flatMap(({ group, item, response }): FormProblem[] => {
      const commentedItem = commentedItems.get(answerAt(group, item));

      // A comment is judged by the item it is about: it is not asked where
      // that item is not, and says nothing where that item is unanswered.
      if (commentedItem !== undefined) {
        const { at: about } = commentedItem;

        if (hidden.has(about.item)) {
          return [
            {
              kind: "answer",
              at: pointerFor([group, item]),
              code: "answered-but-not-asked",
            },
          ];
        }

        return rated.has(answerAt(about.group, about.item))
          ? []
          : [
              {
                kind: "answer",
                at: pointerFor([group, item]),
                code: "comment-without-its-answer",
              },
            ];
      }

      const element = items.find(
        (candidate) =>
          candidate.at.group === group && candidate.at.item === item,
      );

      // The form cannot vouch for an answer filed where it asks no such
      // item: the form comes from another version, or the answer is
      // misplaced.
      if (element === undefined) {
        return [
          {
            kind: "answer",
            at: pointerFor([group, item]),
            code: "not-on-the-form",
          },
        ];
      }

      if (hidden.has(item)) {
        return [
          {
            kind: "answer",
            at: pointerFor([group, item]),
            code: "answered-but-not-asked",
          },
        ];
      }

      // Not answered is what the item says, not an answer a check judges.
      if (isNotAnswered(response)) {
        return [];
      }

      const at = pointerFor([group, item, "response"]);
      const results = element.checks.map((check) =>
        check({ value: response, context }),
      );
      const failures = results.flatMap((result): FormProblem[] =>
        result.isOk() && result.value.verdict === "fails"
          ? [{ ...result.value.problem, kind: "answer", at }]
          : [],
      );

      // A check that cannot judge a value adds nothing a respondent can act
      // on beside a check that has already refused it; alone, it must still
      // refuse, or an unjudged value would pass unnoticed.
      return failures.length > 0
        ? failures
        : results.flatMap((result): FormProblem[] =>
            result.isErr()
              ? [
                  {
                    kind: "answer",
                    at,
                    code: "not-checkable",
                    detail: [
                      result.error.message,
                      ...(result.error.issues ?? []),
                    ].join(": "),
                  },
                ]
              : [],
          );
    });

  return [...unanswered, ...uncommented, ...answerProblems];
};

/**
 * Everything wrong with a document, part-filled or complete, against its
 * instrument and its decorated form, as problems a collector words in its own
 * languages: nothing the instrument refuses, nothing answered that the form
 * did not ask, nothing it insists on left unanswered or without the comment it
 * requires, and every check passing on what it did.
 *
 * Each answer is judged on its own, whatever else is missing: the instrument
 * first, then the form's checks on every response the instrument accepts. Rules
 * and checks are only ever handed responses their items accept. Errs only
 * where no validator can be built from the schema.
 */
export const problemsIn = ({
  schema,
  form,
  document,
  context,
}: {
  schema: PublishedInstrumentSchema;
  form: readonly FormElement[];
  document: SubmissionDocumentUnbranded;
  context: FormContext;
}): Result<FormProblem[], InvalidError> =>
  instrumentIssuesIn(schema, document).map((issues) => [
    ...instrumentProblems(issues, itemsOf(form), document),
    ...formProblems(
      form,
      document,
      refusedAnswersIn(issues, document),
      context,
    ),
  ]);

/**
 * Whether a document is one its decorated form could have produced, as a
 * standard error listing every problem by pointer and code: what a server
 * re-validating a submission answers with.
 */
export const checkDocument = (
  args: Parameters<typeof problemsIn>[0],
): Result<void, InvalidError> =>
  problemsIn(args).andThen((problems) =>
    problems.length === 0
      ? ok(undefined)
      : err(
          buildInvalidError({
            message: "The document is not one its form could have produced",
            issues: problems.map(({ at, code, detail }) =>
              detail === undefined
                ? `${at}: ${code}`
                : `${at}: ${code} (${detail})`,
            ),
          }),
        ),
  );
